<template>
  <section>
    <z-dialog :show="isVisible" :modal-size="modalSize" :d-style="'z-index: 1070'" :bd-style="'z-index: 1065'">
      <template #header>
        <div class="row custom-header">
          <div class="col">
            <h4 class="modal-title">Push LaunchPad Links</h4>
          </div>
          <div class="col-1">
            <a href="#" class="custom-close" @click.prevent="close">
              <i class="fa fa-times"></i>
            </a>
          </div>
        </div>
      </template>
      <template #body>
        <div class="custom-body">
          <div class="row">
            <div class="col">
              <strong>Push these vehicles:</strong>
              <ul class="custom-push-targets">
                <li v-for="(target, index) in pushTargets" :key="index">{{ target.year }} {{ target.model }}</li>
                <li v-if="!pushTargets.length" class="text-danger">No record on screen can be pushed.</li>
              </ul>
              <div v-if="pushSystem">
                Only links for the reported system, <strong>{{ pushSystem }}</strong>, will be pushed for all link types
                (System, Location, Static and Dynamic).
              </div>
              <div v-else>
                All link types (System, Location, Static and Dynamic) for these vehicles will be pushed.
              </div>
              <div>
                If a vehicle is new to a destination, push its Keywords from the Push tab first - links have nothing to
                attach to until the vehicle is there.
              </div>
              <div v-if="pushSkipped.length" class="text-danger mt-1">
                {{ pushSkipped.length }} record(s) have no year or model and cannot be pushed.
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col">
              <span>Destination(s):</span>
              <a href="#" class="ms-2" @click.prevent="selectAllApps">Select all</a>
              <a href="#" class="ms-2" @click.prevent="clearApps">Clear</a>
              <Multiselect
                v-model="pushApps"
                class="form-control custom-multiselect mt-1"
                placeholder="choose destination(s)"
                :open-direction="'bottom'"
                :close-on-select="false"
                :options="apps"
                mode="tags"
                label="appName"
                value-prop="appNumber"
              />
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <div class="custom-footer">
          <div class="col">
            <span v-if="message" :class="isError ? 'text-danger' : 'text-success'">{{ message }}</span>
          </div>
          <div class="col-5 custom-right-align">
            <button v-if="!isPushed" type="button" class="btn btn-light me-2" :disabled="isPushing" @click="close">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!isPushed && !canPush"
              @click="isPushed ? close() : push()"
            >
              <span v-if="isPushing"><i class="fas fa-spinner fa-spin me-1"></i></span>
              {{ isPushed ? 'Close' : 'Push to Destination(s)' }}
            </button>
          </div>
        </div>
      </template>
    </z-dialog>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import Multiselect from '@vueform/multiselect'
import { adasmatrixStore } from '@/stores/adasmatrix'
import { appsStore } from '@/stores/apps'
import ZDialog from '@/components/utilities/elements/Dialog.vue'
import { normalize } from '@/plugins/broken-link-helper'
export default {
  name: 'DialogRepairLinkPush',
  components: {
    ZDialog,
    Multiselect
  },
  props: {
    modelValue: {
      type: Array,
      default: null
    },
    system: {
      type: String,
      default: ''
    }
  },
  emits: ['closing'],
  data() {
    return {
      modalSize: 'modal-xl',
      pushApps: [],
      isAppsLoaded: false,
      isPushing: false,
      isPushed: false,
      message: '',
      isError: false
    }
  },
  watch: {
    modelValue(value) {
      if (value !== null) {
        this.open()
      }
    }
  },
  computed: {
    ...mapState(appsStore, ['apps']),
    isVisible() {
      return this.modelValue !== null
    },
    records() {
      return this.modelValue || []
    },
    pushSystem() {
      return String(this.system || '').trim()
    },
    pushTargets() {
      let targets = []
      this.records.forEach((row) => {
        if (!row.year || !normalize(row.model)) {
          return
        }
        let target = { vehicleMake: row.vehicleMake, year: parseInt(row.year), model: String(row.model).trim(), adasLinks: [] }
        // the api pushes each model exactly, so a variant ("RAV4 Hybrid") is a target of its own
        // rather than riding along with the model it contains
        let same = (kept) =>
          kept.vehicleMake === target.vehicleMake && kept.year === target.year && normalize(kept.model) === normalize(target.model)
        let kept = targets.find(same)
        if (!kept) {
          kept = target
          targets.push(kept)
        }

        if (row.adasLinks) {
          kept.adasLinks.push(parseInt(row.adasLinks))
        }
      })
      return targets.sort((left, right) => left.year - right.year || left.model.localeCompare(right.model))
    },
    pushSkipped() {
      return this.records.filter((row) => !row.year || !normalize(row.model))
    },
    canPush() {
      return !this.isPushing && this.pushApps.length > 0 && this.pushTargets.length > 0
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, ['pushMatrixItems']),
    ...mapActions(appsStore, ['getApps']),
    async open() {
      this.clearMessage()
      this.isPushed = false
      if (!this.isAppsLoaded) {
        await this.getApps('ADAS')
        this.isAppsLoaded = true
      }
      this.selectAllApps()
    },
    close() {
      if (this.isPushing) {
        return
      }
      this.$emit('closing')
    },
    selectAllApps() {
      this.pushApps = this.apps.map((x) => x.appNumber).filter((x) => x)
    },
    clearApps() {
      this.pushApps = []
    },
    clearMessage() {
      this.message = ''
      this.isError = false
    },
    async push() {
      if (!this.canPush) {
        return
      }
      let targets = this.pushTargets
      let sent = 0
      this.isPushing = true
      this.isError = false
      try {
        for (const target of targets) {
          this.message = `Sending ${target.year} ${target.model}...`
          await this.pushMatrixItems({
            items: ['Links'],
            apps: this.pushApps,
            keywords: { isPush: false, keywordMake: null, yearStart: null, yearEnd: null },
            links: {
              isPush: true,
              // no type, so all four of System, Location, Static and Dynamic go
              lookupType: null,
              vehicleYear: target.year,
              vehicleMake: target.vehicleMake,
              vehicleModel: target.model,
              // the report covers one system, so the api sends only that system's links
              system: this.pushSystem || null,
              adasLinks: target.adasLinks
            }
          })
          sent++
        }
        this.message = 'This is a background process; please allow time for the links to reach each application.'
        this.isPushed = true
      } catch {
        this.isError = true
        this.message = `The push failed after ${sent} of ${targets.length} vehicle(s). Please try again.`
      } finally {
        this.isPushing = false
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-header {
  width: 100%;
}
.custom-close {
  float: right;
  color: #000;
  cursor: pointer;
}
.custom-body {
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 500px;
  max-height: 500px;
}
.custom-footer {
  width: 100%;
  display: inline-flex;
  align-items: center;
}
.custom-right-align {
  text-align: right;
}
.custom-push-targets {
  list-style-type: none;
  padding: 0;
  margin: 0.25rem 0;
  font-weight: 500;
}
// size the multiselect like the bootstrap controls beside it - the form-control
// padding would otherwise stack on top of the multiselect's own min-height
.custom-multiselect {
  padding: 0;
  min-height: 38px;
  --ms-px: 0.75rem;
  --ms-radius: 0.375rem;
  --ms-border-color: #ced4da;
}
.custom-multiselect :deep(.multiselect-wrapper) {
  // 38px control less its 1px borders
  min-height: 36px;
}
</style>
